import { BeakerIcon, PlayIcon } from '@heroicons/react/24/outline'
import { Button } from '@duck/ui'
import { Trans } from 'react-i18next'
import { useTar } from '@/modules/i18n/useTar'
import { Logo } from '../Misc/Logo'

interface ManifestoProps {
  onPrimaryCta: () => void
}

export const Manifesto = ({ onPrimaryCta }: ManifestoProps) => {
  const { t } = useTar()

  return (
    <section
      id="manifesto"
      className="relative isolate overflow-hidden border-y border-border py-14 md:py-20"
      aria-labelledby="manifesto-heading"
    >
      <BeakerIcon
        aria-hidden
        className="pointer-events-none absolute -end-12 -top-10 h-72 w-72 text-accent/[0.13] rotate-12 select-none"
      />
      <div className="relative grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14 items-start">
        <div className="space-y-5 text-start">
          <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground">
            {t('manifesto.kicker')}
          </p>
          <div className="flex items-center gap-4">
            <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-accent/10 ring-1 ring-accent/20">
              <Logo short className="text-2xl" />
            </div>
            <h2
              id="manifesto-heading"
              className="text-3xl md:text-4xl font-bold tracking-tight text-foreground"
            >
              {t('manifesto.title')}
            </h2>
          </div>
          <p className="text-muted-foreground text-lg max-w-md">
            {t('manifesto.subtitle')}
          </p>
        </div>
        <div className="w-full text-start">
          <div className="space-y-4 text-muted-foreground leading-relaxed text-start">
            <p>{t('manifesto.p1')}</p>
            <p>{t('manifesto.p2')}</p>
            <p>{t('manifesto.p3')}</p>
            <p>
              <Trans i18nKey="manifesto.p4" components={{ 1: <em /> }} />
            </p>

            <div className="space-y-2">
              <h3 className="text-lg font-semibold text-foreground">
                {t('manifesto.heading1')}
              </h3>
              <p>{t('manifesto.p5')}</p>
              <blockquote className="border-s-2 border-border ps-3 italic text-foreground">
                {t('manifesto.quote1')}
              </blockquote>
              <p>{t('manifesto.p6')}</p>
              <p>{t('manifesto.p7')}</p>
            </div>

            <div className="space-y-2">
              <h3 className="text-lg font-semibold text-foreground">
                {t('manifesto.heading2')}
              </h3>
              <p>{t('manifesto.p8')}</p>
              <p>{t('manifesto.p9')}</p>
              <p>{t('manifesto.p10')}</p>
              <p>{t('manifesto.p11')}</p>
            </div>

            <div className="space-y-2">
              <h3 className="text-lg font-semibold text-foreground">
                {t('manifesto.heading3')}
              </h3>
              <p>{t('manifesto.p12')}</p>
              <p>{t('manifesto.p13')}</p>
              <p>{t('manifesto.p14')}</p>
            </div>

            <div className="space-y-2">
              <h3 className="text-lg font-semibold text-foreground">
                {t('manifesto.heading4')}
              </h3>
              <p>{t('manifesto.p15')}</p>
              <p>{t('manifesto.p16')}</p>
              <p>{t('manifesto.p17')}</p>
              <p>{t('manifesto.p18')}</p>
              <p>{t('manifesto.p19')}</p>
              <p>{t('manifesto.p20')}</p>
              <p>{t('manifesto.p21')}</p>
              <p>{t('manifesto.p22')}</p>
            </div>

            <div className="space-y-2">
              <h3 className="text-lg font-semibold text-foreground">
                {t('manifesto.heading5')}
              </h3>
              <p>{t('manifesto.p23')}</p>
              <p>{t('manifesto.p24')}</p>
              <p>{t('manifesto.p25')}</p>
            </div>

            <div className="space-y-2">
              <h3 className="text-lg font-semibold text-foreground">
                {t('manifesto.heading6')}
              </h3>
              <p>{t('manifesto.p26')}</p>
              <p>{t('manifesto.p27')}</p>
              <p>{t('manifesto.p28')}</p>
            </div>

            <div className="space-y-2">
              <h3 className="text-lg font-semibold text-foreground">
                {t('manifesto.heading7')}
              </h3>
              <p>{t('manifesto.p29')}</p>
              <p>{t('manifesto.p30')}</p>
              <p>{t('manifesto.p31')}</p>
              <p>{t('manifesto.p32')}</p>
              <p>{t('manifesto.p33')}</p>
              <p>{t('manifesto.p34')}</p>
            </div>

            <div className="flex justify-center pt-4">
              <Button
                onClick={onPrimaryCta}
                className="inline-flex items-center gap-2 px-6 py-3 bg-accent hover:bg-accent/80 text-foreground text-lg font-medium rounded-lg transition-colors duration-200"
              >
                <PlayIcon className="w-6 h-6" />
                <span>{t('home.getStartedNow')}</span>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
